import { z } from "zod";

/**
 * Every enum that crosses the API boundary or lands in a Postgres enum column.
 * The string values here are the exact values stored in the database, so changing
 * one is a migration, not a refactor.
 */

export const businessStatuses = ["draft", "active", "suspended"] as const;
export const businessStatusSchema = z.enum(businessStatuses);
export type BusinessStatus = z.infer<typeof businessStatusSchema>;

/**
 * `owner` and `admin` run the business; `staff` sees the schedule, rosters and member threads and
 * nothing about money. Labelled "coach" or "instructor" on screen depending on the business, per
 * `business_labels`, but the system name and the stored value are always `staff`.
 *
 * `frontdesk` (migration 0080, Pilates studio shape T1) works the desk: books for a client, records
 * a manual payment, checks people in and approves a new client. Never packages, rules, the
 * timetable, the team, the Teamna plan, payouts or the bank account. Every business shape has it.
 * "Front desk" on screen. The permission map is `packages/shared/src/desk-permissions.ts`; the owner
 * gives it more in Settings, Front desk (`front-desk-access.ts`).
 */
export const businessRoles = ["owner", "admin", "staff", "frontdesk"] as const;
export const businessRoleSchema = z.enum(businessRoles);
export type BusinessRole = z.infer<typeof businessRoleSchema>;

/** Owners and admins run the business: every path of Desk. */
function runsBusiness(role: BusinessRole | null): boolean {
  return role === "owner" || role === "admin";
}

/**
 * The Front desk role (migration 0080, T1): books for a client, records a manual payment, checks
 * people in and approves a new client. Never packages, rules, the timetable, the team, the Teamna
 * plan, payouts or the bank account. The paths it may call are in the permission map
 * (`desk-permissions.ts`); Desk and the phone show a job only when `deskMay` allows it.
 */
export function isFrontDesk(role: BusinessRole | null | undefined): boolean {
  return role === "frontdesk";
}

/**
 * The roles that work the desk: the owner, the admins and the front desk. They open the owner
 * office (the front desk a part of it), see the clients' balances, packs and what they owe, and
 * book and mark attendance on every session, not only on the sessions they run.
 */
export function worksTheDesk(role: BusinessRole | null | undefined): boolean {
  return runsBusiness(role ?? null) || isFrontDesk(role);
}

/** The Front desk role in a sentence (T1): "added you to the team, as a person at the front desk". */
export const frontDeskRoleWords = "person at the front desk";

/** The words for each role, where the business's own staff word is not known (support, Control Center, Slack). */
export const businessRoleWords: Record<BusinessRole, string> = { owner: "Owner", admin: "Admin", staff: "Staff", frontdesk: "Front desk" };

/** The roles the owner gives to a person on the team: every role but `owner`. */
export const teamRoles = ["admin", "staff", "frontdesk"] as const;
export const teamRoleSchema = z.enum(teamRoles);
export type TeamRole = z.infer<typeof teamRoleSchema>;

/** The team role of a `business_members` row. An owner row has none; the callers read it as `staff`. */
export function teamRoleOf(role: BusinessRole): TeamRole {
  return role === "owner" ? "staff" : role;
}

/** Roles a route guard can require. "member" means an active membership, not a business_members row. */
export const actorRoles = ["owner", "staff", "member"] as const;
export const actorRoleSchema = z.enum(actorRoles);
export type ActorRole = z.infer<typeof actorRoleSchema>;

export const inviteKinds = ["member", "staff"] as const;
export const inviteKindSchema = z.enum(inviteKinds);
export type InviteKind = z.infer<typeof inviteKindSchema>;

/** `pending` is a member who joined with the business join code but was not on the imported list, waiting for the owner. */
export const membershipStatuses = ["active", "paused", "left", "pending"] as const;
export const membershipStatusSchema = z.enum(membershipStatuses);
export type MembershipStatus = z.infer<typeof membershipStatusSchema>;

export const participantKinds = ["self", "child"] as const;
export const participantKindSchema = z.enum(participantKinds);
export type ParticipantKind = z.infer<typeof participantKindSchema>;

export const bookingModels = ["recurring", "open"] as const;
export const bookingModelSchema = z.enum(bookingModels);
export type BookingModel = z.infer<typeof bookingModelSchema>;

export const lateCancelRules = ["lose_credit", "return_credit", "partial"] as const;
export const lateCancelRuleSchema = z.enum(lateCancelRules);
export type LateCancelRule = z.infer<typeof lateCancelRuleSchema>;

export const sessionStatuses = ["scheduled", "cancelled", "moved"] as const;
export const sessionStatusSchema = z.enum(sessionStatuses);
export type SessionStatus = z.infer<typeof sessionStatusSchema>;

export const enrollmentStatuses = ["active", "paused", "ended"] as const;
export const enrollmentStatusSchema = z.enum(enrollmentStatuses);
export type EnrollmentStatus = z.infer<typeof enrollmentStatusSchema>;

/** `walk_in` (migration 0038): the coach added a swimmer who came and was not booked. */
export const bookingSources = ["enrollment", "open", "walk_in"] as const;
export const bookingSourceSchema = z.enum(bookingSources);
export type BookingSource = z.infer<typeof bookingSourceSchema>;

export const bookingStatuses = [
  "confirmed",
  "cancelled_by_member",
  "cancelled_late",
  "cancelled_by_business",
  "rescheduled",
] as const;
export const bookingStatusSchema = z.enum(bookingStatuses);
export type BookingStatus = z.infer<typeof bookingStatusSchema>;

export const purchaseStatuses = ["active", "exhausted", "expired", "refunded"] as const;
export const purchaseStatusSchema = z.enum(purchaseStatuses);
export type PurchaseStatus = z.infer<typeof purchaseStatusSchema>;

/** Why the business paused a package (migration 0071). The rules are in `package-freeze.ts`. */
export const packageFreezeReasons = ["travel", "injury", "pregnancy", "other"] as const;
export const packageFreezeReasonSchema = z.enum(packageFreezeReasons);
export type PackageFreezeReason = z.infer<typeof packageFreezeReasonSchema>;

/**
 * What a package product is (migration 0079): a block of sessions (`pack`), or a monthly
 * membership with a class cap (`membership`). Rules: `membership.ts`.
 */
export const packageKinds = ["pack", "membership"] as const;
export const packageKindSchema = z.enum(packageKinds);
export type PackageKind = z.infer<typeof packageKindSchema>;

/** Who cancelled a membership (migration 0079). */
export const membershipCancelBys = ["member", "owner"] as const;
export const membershipCancelBySchema = z.enum(membershipCancelBys);
export type MembershipCancelBy = z.infer<typeof membershipCancelBySchema>;

/** Why a membership ended (migration 0079). */
export const membershipEndReasons = ["period_end", "now", "unpaid"] as const;
export const membershipEndReasonSchema = z.enum(membershipEndReasons);
export type MembershipEndReason = z.infer<typeof membershipEndReasonSchema>;

export const creditReasons = [
  "purchase",
  "booking",
  "cancel_return",
  "late_cancel_partial",
  /** A late cancellation that spent one of the package's free late cancellations to come back. */
  "late_cancel_waived",
  "business_cancel_return",
  "owner_adjustment",
  "expiry",
  /** The number of sessions a family had left when the business moved onto Teamna mid-period. */
  "starting_balance",
  /**
   * The unused sessions of a package moved to the package that waits for it (card #1): a minus line
   * on the old package, a plus line on the next one. Migration 0086.
   */
  "carry_over",
] as const;
export const creditReasonSchema = z.enum(creditReasons);
export type CreditReason = z.infer<typeof creditReasonSchema>;

export const recurringFeeIntervals = ["monthly", "term"] as const;
export const recurringFeeIntervalSchema = z.enum(recurringFeeIntervals);
export type RecurringFeeInterval = z.infer<typeof recurringFeeIntervalSchema>;

/** `one_off` is an amount the owner asks one member for, paid in the app or by a payment link. */
export const paymentRequestKinds = ["package", "recurring_fee", "one_off"] as const;
export const paymentRequestKindSchema = z.enum(paymentRequestKinds);
export type PaymentRequestKind = z.infer<typeof paymentRequestKindSchema>;

export const paymentRequestStatuses = ["pending", "paid", "overdue", "void"] as const;
export const paymentRequestStatusSchema = z.enum(paymentRequestStatuses);
export type PaymentRequestStatus = z.infer<typeof paymentRequestStatusSchema>;

export const paymentStatuses = [
  "pending",
  "succeeded",
  "failed",
  "refunded",
  "partially_refunded",
] as const;
export const paymentStatusSchema = z.enum(paymentStatuses);
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

/**
 * How the money came in. `card` is the online checkout. `cash` and `transfer` are recorded by the
 * owner at the desk (product-intent 3.4): no provider, no Teamna fee, refunds are only recorded.
 */
export const paymentMethods = ["card", "cash", "transfer"] as const;
export const paymentMethodSchema = z.enum(paymentMethods);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

/** The two methods an owner records by hand. */
export const manualPaymentMethods = ["cash", "transfer"] as const;
export const manualPaymentMethodSchema = z.enum(manualPaymentMethods);
export type ManualPaymentMethod = z.infer<typeof manualPaymentMethodSchema>;

export const refundStatuses = ["pending", "succeeded", "failed"] as const;
export const refundStatusSchema = z.enum(refundStatuses);
export type RefundStatus = z.infer<typeof refundStatusSchema>;

/**
 * The business's payment account (Stripe Connect). `pending` reads "in progress" and `complete`
 * reads "verified" on screen. Derived from the provider by `deriveOnboardingStatus`
 * (`payout-account.ts`), never set by hand.
 */
export const paymentOnboardingStatuses = [
  "not_started",
  "pending",
  "needs_information",
  "complete",
  "restricted",
] as const;
export const paymentOnboardingStatusSchema = z.enum(paymentOnboardingStatuses);
export type PaymentOnboardingStatus = z.infer<typeof paymentOnboardingStatusSchema>;

export const notificationChannels = ["push", "whatsapp", "email"] as const;
export const notificationChannelSchema = z.enum(notificationChannels);
export type NotificationChannel = z.infer<typeof notificationChannelSchema>;

export const notificationStatuses = [
  "scheduled",
  "sent",
  "delivered",
  "failed",
  "cancelled",
] as const;
export const notificationStatusSchema = z.enum(notificationStatuses);
export type NotificationStatus = z.infer<typeof notificationStatusSchema>;

/**
 * Template keys. Push uses them to pick copy locally; WhatsApp maps them one to one onto the
 * approved Meta template names, so renaming a key means re-submitting a template for approval.
 * Template copy reads business_labels at render time; the keys below never carry a sport word.
 */
export const notificationTemplateKeys = [
  "session_reminder_24h",
  "session_reminder_2h",
  "schedule_change",
  "session_cancelled_by_business",
  "booking_confirmed",
  /**
   * Several sessions booked in one go (the month booking). Payload carries `count`, `bookingIds` and
   * `summary`, one line a person: "Lina: 4 sessions, Mon 12 Oct to Thu 5 Nov." Push only.
   */
  "bookings_confirmed",
  /**
   * The worker booked a place that opened on a waiting list (migration 0061). Payload as
   * `booking_confirmed`. Push only.
   */
  "waitlist_booked",
  /**
   * A place opened on a waiting list, but the booking was refused (no session left, a session at the
   * same time). Payload: `participantName`, `groupName`, `when`, `reason`. Push only.
   */
  "waitlist_missed",
  /**
   * A late place on a waiting list (B2): the person can accept it until `acceptBy`. Payload:
   * `waitlistEntryId`, `sessionId`, `participantName`, `groupName`, `when`, `acceptBy` (both in the
   * business timezone), `url`. Push, and WhatsApp with the opt-in (the Meta template needs approval).
   */
  "waitlist_offer",
  /**
   * The owner published a timetable change that touches this account's bookings: times that moved,
   * sessions that came off. One message per account per publish. Payload carries `summary` (the
   * lines for this account), `bookingIds` and `creditsReturned`. Push only.
   */
  "timetable_changed",
  "booking_cancelled",
  "booking_cancelled_by_member",
  "payment_due",
  "payment_received",
  "package_near_expiry",
  "low_credits",
  "invite",
  /**
   * The welcome before the first class of a person at a business (C3, `jobs/first-class-welcome.ts`).
   * One for each participant and business, ever. Payload: `businessName`, `participantName`,
   * `groupName`, `when`, `message` (the owner's text), `sessionId`. Push, and WhatsApp for an account
   * that said yes to WhatsApp (the Meta template `first_class_welcome`).
   */
  "first_class_welcome",
  /**
   * The owner asked this coach to add their swimmers (coach-adds-swimmers). Payload carries
   * `ownerName`, `businessName`, `url`. Push only: the WhatsApp goes out as a Whapi text.
   */
  "staff_asked_to_add_participants",
  /** A coach added swimmers for the owner to link. Payload carries `staffName`, `count`, `url`. Push only. */
  "participants_added_by_staff",
  /** A swimmer came to a session and was not booked. Payload: `staffName`, `name`, `groupName`, `when`. Push only. */
  "participant_came_unbooked",
  /** The owner removed a name the coach added on the pool deck. Payload: `ownerName`, `name`, `groupName`, `when`. Push only. */
  "session_extra_removed",
  // Cover requests between staff (migration 0072), to the team only, push only: no WhatsApp
  // template exists. Payload carries `groupName`, `when`, `askerName`, `takerName`, `sessionId`,
  // `coverId`.
  /** A staff member asks for cover, to each person who got the offer. */
  "cover_offered",
  /** A person took the cover: to the person who asked, and to the owner and admins. */
  "cover_taken",
  /** A person said yes and the owner approves each cover: to the owner and admins. */
  "cover_needs_approval",
  /** The owner refused a yes: to the person who said it. */
  "cover_refused",
  /** A direct message landed in a thread the recipient is part of. Payload carries `conversationId`. */
  "message_received",
  /** The business posted to everyone. Payload carries `conversationId`. */
  "announcement",
  /**
   * A sign-in method was added to or removed from the account. Payload carries `change`
   * (`added` or `removed`), `kind` and `masked`. Push and email only: no WhatsApp template exists.
   */
  "sign_in_method_added",
  // Teamna subscription billing, to the owner and admins of a business, by email only (spec section
  // 7). Payload carries `businessName`, `billingUrl` and the dates as text. No WhatsApp template.
  /** The trial ends soon and there is no card. `days` is 3 or 1, `endsOn` the date. */
  "billing_trial_ending",
  /** The trial ended with no card, or a charge failed. `reason` is `trial_ended` or `payment_failed`, `graceEndsOn` the date. */
  "billing_payment_failed",
  /** The grace is over: Desk is read-only for the owner and admins. */
  "billing_read_only",
  /** A payment ended the grace or the read-only time. */
  "billing_restored",
  /**
   * The business added or changed the profile photo of a participant (migration 0029). To the
   * family account. Payload carries `businessName`, `participantName` and `participantId`. Push and
   * the in-app feed only: no WhatsApp template.
   */
  "profile_photo_added",
  // Card payments to the business (Stripe Connect), to the owner and admins, by email only. Payload
  // carries `businessName` and `getPaidUrl`. No WhatsApp template.
  /** Stripe verified the business: card payments are on. */
  "payout_account_verified",
  /** Stripe needs more information. `tasks` is the count, `deadlineOn` the date as text or empty. */
  "payout_account_needs_information",
  /** A Stripe deadline is near. `days` is 7, 3 or 1, `deadlineOn` the date. */
  "payout_account_deadline",
  /** Stripe stopped card payments for the business. */
  "payout_account_stopped",
  /** The bank account on the business's Stripe account changed. `bankName`, `bankLast4`. */
  "payout_account_bank_changed",
  /** A member's bank disputed a card payment. `amount`, `payerName`, `reason`. */
  "payment_disputed",
] as const;
export const notificationTemplateKeySchema = z.enum(notificationTemplateKeys);
export type NotificationTemplateKey = z.infer<typeof notificationTemplateKeySchema>;

/** The owner emails of the Teamna plan (subscription billing spec, section 7). A pilot gets none of them. */
export const billingEmailTemplateKeys: readonly NotificationTemplateKey[] = ["billing_trial_ending", "billing_payment_failed", "billing_read_only", "billing_restored"];

/**
 * A `direct` conversation is one member's private thread with one business, readable by every staff
 * row of that business. An `announcement` conversation is the business's one-way channel to every member.
 */
export const conversationKinds = ["direct", "announcement"] as const;
export const conversationKindSchema = z.enum(conversationKinds);
export type ConversationKind = z.infer<typeof conversationKindSchema>;

/** How a sign-in code travels. `sms` and `whatsapp` go to a phone number, `email` to an address. */
export const signInChannels = ["sms", "whatsapp", "email"] as const;
export const signInChannelSchema = z.enum(signInChannels);
export type SignInChannel = z.infer<typeof signInChannelSchema>;

export const phoneSignInChannels = ["sms", "whatsapp"] as const;
export const phoneSignInChannelSchema = z.enum(phoneSignInChannels);
export type PhoneSignInChannel = z.infer<typeof phoneSignInChannelSchema>;

export const signInMethodKinds = ["phone", "email"] as const;
export const signInMethodKindSchema = z.enum(signInMethodKinds);
export type SignInMethodKind = z.infer<typeof signInMethodKindSchema>;

export const devicePlatforms = ["ios", "android"] as const;
export const devicePlatformSchema = z.enum(devicePlatforms);
export type DevicePlatform = z.infer<typeof devicePlatformSchema>;

export const paymentProviders = ["fake", "stripe"] as const;
export const paymentProviderSchema = z.enum(paymentProviders);
export type PaymentProviderName = z.infer<typeof paymentProviderSchema>;

/** Policy engine vocabulary. Kept here so the mobile app can switch on it without importing the engine. */
export const policyActions = ["cancel", "reschedule"] as const;
export const policyActionSchema = z.enum(policyActions);
export type PolicyAction = z.infer<typeof policyActionSchema>;

export const policyActors = ["member", "business"] as const;
export const policyActorSchema = z.enum(policyActors);
export type PolicyActor = z.infer<typeof policyActorSchema>;

/**
 * Which cancellations spend a package's free cancellations. The package sets it next to the number,
 * and the purchase keeps its own copy, because the mode says what the number means.
 *
 * - `any_cancel`: every cancellation that gives the session back spends one, early or late. When
 *   none are left, every cancellation uses the session up, however early. The behaviour before
 *   the mode existed, so every older package and purchase has it.
 * - `late_only`: a cancellation before the cut-off is always free and spends nothing. A late
 *   cancellation spends one, and that one gives the session back. When none are left, the late
 *   rule decides.
 */
export const freeCancellationModes = ["any_cancel", "late_only"] as const;
export const freeCancellationModeSchema = z.enum(freeCancellationModes);
export type FreeCancellationMode = z.infer<typeof freeCancellationModeSchema>;

export const policyPenalties = ["none", "lose_credit", "partial"] as const;
export const policyPenaltySchema = z.enum(policyPenalties);
export type PolicyPenalty = z.infer<typeof policyPenaltySchema>;

export const policyReasons = [
  "before_cutoff",
  "after_cutoff",
  "business_cancel",
  "reschedule_limit_booking",
  "reschedule_limit_month",
  "session_started",
  "cancellation_allowance_used",
  /** A late cancellation that a free late cancellation paid for ("late_only" packages). */
  "late_cancel_waived",
] as const;
export const policyReasonSchema = z.enum(policyReasons);
export type PolicyReason = z.infer<typeof policyReasonSchema>;

export const bookingBlockReasons = [
  "too_soon",
  "too_far_ahead",
  "session_started",
  "session_cancelled",
] as const;
export const bookingBlockReasonSchema = z.enum(bookingBlockReasons);
export type BookingBlockReason = z.infer<typeof bookingBlockReasonSchema>;

export const auditActions = [
  "business.created",
  "business.updated",
  "business.labels_updated",
  /** Who books or how classes fill changed (migration 0069), in Settings > Business. */
  "business.shape_changed",
  /** The health form or the terms changed (C2, migration 0077), in Settings > Health. No answer text. */
  "business.health_form_changed",
  // Control Center: the "Warm intro" tag on or off (task 0.7).
  "business.warm_intro_tagged",
  "business.logo_uploaded",
  "business.logo_changed",
  /** The colour of the Teamna app changed (migration 0051). */
  "business.app_accent_changed",
  /** A profile photo was added or replaced (migration 0029). `after` names the person. */
  "profile_photo.set",
  "profile_photo.removed",
  /** A family turned "no profile photo" on or off for a participant. */
  "profile_photo.consent_changed",
  "policy.created",
  "policy.updated",
  /** The owner sent a no-show or late-cancel fee (a one-off payment request) or waived it (migration 0078). */
  "booking_fee.sent",
  "booking_fee.waived",
  "group.created",
  "group.merged",
  "group.updated",
  /** A group got its own booking window or free-cancel time, or went back to the business rules (migration 0073). */
  "group.rules_changed",
  "schedule_template.created",
  "schedule_template.updated",
  /** The owner published the timetable: weekly times, the end of the term, closed dates. */
  "timetable.published",
  "timetable.month_published",
  "session.created",
  "session.moved",
  "session.cancelled",
  "membership.updated",
  /**
   * A parent joined without a code (`services/join-offers.ts`): at sign-in, when the business added
   * the family, or from the join screen (an invitation, or "Ask to join again" after a membership ended).
   */
  "membership.joined",
  "enrollment.created",
  "enrollment.updated",
  "credits.adjusted",
  /** The owner or an admin paused a package: its end date moved by the paused days (migration 0071). */
  "package.frozen",
  /** The owner or an admin ended a pause early: the end date moved back by the days not used. */
  "package.unfrozen",
  /** The owner or an admin moved the last day of one package to a later day (card #1). */
  "package.extended",
  /** The unused sessions of a package moved to the package that waits for it (card #1, migration 0086). */
  "package.carried_over",
  /** A monthly membership was cancelled at the end of the month, or ended at once (M2, migration 0079). */
  "membership.cancelled",
  /** The job ended a membership: the cancelled month ended, or the renewal was not paid (M2). */
  "membership.ended",
  "payment.refunded",
  /** The owner recorded a cash or bank transfer payment. */
  "payment.recorded",
  /** The owner or an admin attached, replaced or removed the proof of a payment recorded before (migration 0084). */
  "payment.proof_changed",
  /** The owner sold a package to a member at the desk, paid or to pay later. */
  "package.sold",
  /** A second payment for a balance or a request that was already paid or cancelled. Support refunds it. */
  "payment.needs_refund",
  /** The owner asked one family for an amount (a one-off payment request with a payment link). */
  "payment_request.created",
  /** The owner cancelled a one-off payment request; its payment link closed. */
  "payment_request.cancelled",
  /** The business's payment account (Stripe Connect) was made. */
  "payment_account.created",
  /** The state of the payment account changed (read from the provider). */
  "payment_account.status_changed",
  /** The bank account on the business's payment account changed (read from the provider). */
  "payment_account.bank_changed",
  /** A member's bank disputed a card payment (a chargeback). */
  "payment.disputed",
  "package_product.created",
  "package_product.updated",
  "recurring_fee.created",
  "announcement.sent",
  "clients.messaged",
  "participant.placed",
  "participant.confirmed",
  /** Families page (migration 0024). */
  "families.added",
  "families.undone",
  "family.updated",
  "family.removed",
  "family.restored",
  "family.adult_added",
  "family.adult_removed",
  "family.linked",
  "family.link_undone",
  "family.child_added",
  /** A coach typed names under a level (migration 0037). `after` has the count and the level. */
  "pending_participant.added",
  /** A coach said a name of the level is theirs too ("Same swimmer"). */
  "pending_participant.claimed",
  "pending_participant.updated",
  "pending_participant.removed",
  /** The owner linked a waiting name to a parent or made the swimmer the account holder. */
  "pending_participant.linked",
  "pending_participant.unlinked",
  "parent_matches.found",
  "parent_matches.dismissed",
  /** The owner copied the message (or the reminder) for the parents' group (migration 0045). */
  "parents.asked",
  /** A parent answered on the find page: these names are my children. */
  "parent_claim.created",
  /** The owner linked an answer, or said No to it. */
  "parent_claim.linked",
  "parent_claim.rejected",
  /** The owner asked a coach on WhatsApp for the parent of a name; the coach answered. */
  "parent_ask.sent",
  "parent_ask.answered",
  /** The owner asked coaches to add their swimmers. `after` has the results per coach. */
  "staff.asked_to_add_participants",
  "team.invitation_sent",
  "team.invitation_revoked",
  /** The coach added a swimmer who came and was not booked (migration 0038). */
  "session_extra.added",
  "session_extra.removed",
  /** The owner took one session, or let it go, for a swimmer who came and was not booked. */
  "session_extra.decided",
  "family.child_updated",
  "family.child_removed",
  "family.child_restored",
  "family.hold_set",
  "family.hold_cleared",
  "family.invited",
  /** A message from Home: we miss you, a reminder to pay, a renewal. */
  "nudge.sent",
  "starting_balance.saved",
  "booking.created_by_business",
  "client.told_about_session",
  "booking.cancelled_by_business",
  "booking.attendance_marked",
  "invite.sent",
  "contacts.imported",
  "contacts.sorted",
  "contacts.kept",
  "contacts.archived",
  "contacts.restored",
  "contacts.swimmers_linked",
  "contacts.imported_csv",
  "contacts.imported_csv_applied",
  "session.staff_changed",
  "session.running_late",
  /** Cover requests between staff (migration 0072). */
  "cover.requested",
  "cover.taken",
  "cover.declined",
  "cover.approved",
  "cover.refused",
  "cover.cancelled",
  "user.signed_in",
  "user.sign_in_method_added",
  "user.sign_in_method_removed",
  /** The person deleted their own account (slice 0.2). The row stays, scrubbed. */
  "account.deleted",
  /** Teamna subscription billing: the owner or an admin changed the billing email. */
  "billing.email_changed",
  // Control Center billing actions (spec section 6.2), with the staff email in `actor_staff_email`.
  "billing.trial_changed",
  "billing.discount_applied",
  "billing.discount_removed",
  "billing.access_kept",
  "billing.refunded",
  "billing.paused",
  "billing.resumed",
  "billing.credit_added",
  "billing.price_moved",
  "billing.pilot_started",
  "billing.pilot_ended",
  /** Control Center deleted the business. The row has no business id: the business is gone. */
  "business.deleted",
  /** Control Center turned card payments on or off for all of Teamna. The row has no business id. */
  "platform.card_payments_switched",
  /** Control Center turned the "Runs on Teamna" link on or off for one business. */
  "business.runs_on_teamna_switched",
  /**
   * Control Center gave AI Assistant free to one business, changed the last day, or took the grant
   * back (ai-assistant-v1.md, "The package").
   */
  "business.ai_assistant_granted",
  /** The owner or an admin added the AI Assistant package in Desk, removed it, or kept it after a remove. */
  "business.ai_assistant_package_changed",
  /** A Teamna staff member read one AI Assistant chat in Control Center (AI Assistant review). */
  "assistant.conversation_viewed",
  /**
   * The owner confirmed an AI Assistant proposal that sends a message: a payment reminder or a
   * check-in to a family. Session changes keep their own actions (`session.cancelled` and others).
   */
  "assistant.message_sent",
  /** Support tickets (support-system.md): a person at the business wrote to Teamna. */
  "support_ticket.created",
  /** Control Center moved a ticket to another column, with the staff email in `actor_staff_email`. */
  "support_ticket.moved",
  /** Control Center replied to the sender, or wrote a team note. The text stays in the thread. */
  "support_ticket.replied",
  "support_ticket.noted",
  /** Control Center changed the labels of a ticket. */
  "support_ticket.tags_changed",
  /** Control Center changed the team member on a ticket, or its team priority. */
  "support_ticket.assigned",
  "support_ticket.priority_changed",
  /** A person at the business said if the answer solved the request. */
  "support_ticket.rated",
  /** Control Center added or changed a label, or changed a board column. No business id. */
  "support_tag.created",
  "support_tag.updated",
  "support_status.updated",
  /** Control Center added a board column, or changed the order of the columns. No business id. */
  "support_status.created",
  "support_status.reordered",
  /** Control Center added, changed or deleted a saved reply. No business id. */
  "support_saved_reply.created",
  "support_saved_reply.updated",
  "support_saved_reply.deleted",
] as const;
export const auditActionSchema = z.enum(auditActions);
export type AuditAction = z.infer<typeof auditActionSchema>;

// ---------------------------------------------------------------------------
// Business setup (mobile app V1 spec)
// ---------------------------------------------------------------------------

/**
 * The role a person has at the business, as they say it on the owner sign-up (migration 0049). A
 * label only: the access comes from `businessRole`.
 */
export const jobTitles = ["owner", "manager", "head_coach"] as const;
export const jobTitleSchema = z.enum(jobTitles);
export type JobTitle = z.infer<typeof jobTitleSchema>;
export const jobTitleLabels: Record<JobTitle, string> = { owner: "Owner", manager: "Manager", head_coach: "Head coach" };

export const sports = ["swimming", "pilates", "padel", "football", "gymnastics", "other"] as const;
export const sportSchema = z.enum(sports);
export type Sport = z.infer<typeof sportSchema>;

/**
 * The one list of business types, with the words people see. Create business and the business step
 * on Desk, the mobile onboarding, and the website form all read it, in this order.
 */
export const sportLabels: Record<Sport, string> = {
  swimming: "Swimming",
  pilates: "Pilates",
  padel: "Padel",
  football: "Football",
  gymnastics: "Gymnastics",
  other: "Other",
};

export const sportOptions: ReadonlyArray<{ id: Sport; label: string }> = sports.map((id) => ({ id, label: sportLabels[id] }));

/**
 * The business shape (migration 0069): two switches that decide how Teamna behaves. Screens read
 * the shape, never the sport. The sport only sets the start values (`defaultShape`).
 *
 * `booking_for`: who a member books for. `children`: a member books for their children (a family).
 * `self`: a member books for themselves.
 */
export const bookingForValues = ["children", "self"] as const;
export const bookingForSchema = z.enum(bookingForValues);
export type BookingFor = z.infer<typeof bookingForSchema>;

/**
 * `class_fill`: how classes fill. `fixed_group`: the business puts each participant in one group,
 * and the participant books the sessions of that group. `pick_class`: a member picks each session.
 */
export const classFillValues = ["fixed_group", "pick_class"] as const;
export const classFillSchema = z.enum(classFillValues);
export type ClassFill = z.infer<typeof classFillSchema>;

/**
 * A group is what members read as a class or level; a private slot holds one participant with one
 * staff member, or two for a duet (a private for two, Studio S2: `privateSeats`).
 */
export const classKinds = ["group", "private"] as const;
export const classKindSchema = z.enum(classKinds);
export type ClassKind = z.infer<typeof classKindSchema>;

/**
 * The short level on a class (migration 0074, Studio S2), shown as a badge where members book and
 * on the class panel. Optional: null is no level and no badge. The words are `classLevelWords`.
 */
export const classLevels = ["all_levels", "beginner", "intermediate", "advanced"] as const;
export const classLevelSchema = z.enum(classLevels);
export type ClassLevel = z.infer<typeof classLevelSchema>;

export const placementStatuses = ["placed", "unsure", "unplaced"] as const;
export const placementStatusSchema = z.enum(placementStatuses);
export type PlacementStatus = z.infer<typeof placementStatusSchema>;

/** A name a coach typed (migration 0037): waiting for the owner, linked to a participant, or removed. */
export const pendingParticipantStatuses = ["waiting", "linked", "removed"] as const;
export const pendingParticipantStatusSchema = z.enum(pendingParticipantStatuses);
export type PendingParticipantStatus = z.infer<typeof pendingParticipantStatusSchema>;

/** How the owner linked a waiting name (migration 0041). `contact`: a person from the synced WhatsApp group. */
export const pendingLinkKinds = ["family", "new_family", "self", "participant", "contact"] as const;
export const pendingLinkKindSchema = z.enum(pendingLinkKinds);
export type PendingLinkKind = z.infer<typeof pendingLinkKindSchema>;

/** Who found the parent of a linked name: the owner, a match from Teamna (migration 0041), or the answer of a parent or a coach (migration 0045). */
export const pendingLinkedVias = ["owner", "match", "parent", "coach"] as const;
export const pendingLinkedViaSchema = z.enum(pendingLinkedVias);
export type PendingLinkedVia = z.infer<typeof pendingLinkedViaSchema>;

/** How sure a parent match is. `siblings`: Teamna found siblings, and no parent in the group. */
export const parentMatchConfidences = ["likely", "maybe", "siblings"] as const;
export const parentMatchConfidenceSchema = z.enum(parentMatchConfidences);
export type ParentMatchConfidence = z.infer<typeof parentMatchConfidenceSchema>;

/** What made a parent match: the language model (`ai`), or the family-name rule when no model is set up. */
export const parentMatchSources = ["ai", "rule"] as const;
export const parentMatchSourceSchema = z.enum(parentMatchSources);
export type ParentMatchSource = z.infer<typeof parentMatchSourceSchema>;

/** Who answered "Ask the parents" (migration 0045): the parent on the find page, or a coach (a later PR). */
export const parentClaimSources = ["parent", "coach"] as const;
export const parentClaimSourceSchema = z.enum(parentClaimSources);
export type ParentClaimSource = z.infer<typeof parentClaimSourceSchema>;

export const parentClaimStatuses = ["new", "linked", "rejected"] as const;
export const parentClaimStatusSchema = z.enum(parentClaimStatuses);
export type ParentClaimStatus = z.infer<typeof parentClaimStatusSchema>;

/** A question to a coach about a parent (migration 0046). */
export const parentAskStatuses = ["open", "answered", "dont_know"] as const;
export const parentAskStatusSchema = z.enum(parentAskStatuses);
export type ParentAskStatus = z.infer<typeof parentAskStatusSchema>;

export const parentClaimRelations = ["mother", "father", "other"] as const;
export const parentClaimRelationSchema = z.enum(parentClaimRelations);
export type ParentClaimRelation = z.infer<typeof parentClaimRelationSchema>;

/** What WhatsApp answered for one "Ask coaches" message. `no_phone`: the coach has no mobile. */
export const staffRequestWhatsappStatuses = ["sent", "failed", "no_phone", "not_configured"] as const;
export const staffRequestWhatsappStatusSchema = z.enum(staffRequestWhatsappStatuses);
export type StaffRequestWhatsappStatus = z.infer<typeof staffRequestWhatsappStatusSchema>;

/** A personal team invitation (migration 0054). `replaced`: a newer one took its place. `removed`: the owner removed the person. */
export const teamInvitationStatuses = ["pending", "accepted", "revoked", "removed", "replaced"] as const;
export const teamInvitationStatusSchema = z.enum(teamInvitationStatuses);
export type TeamInvitationStatus = z.infer<typeof teamInvitationStatusSchema>;

/** Attendance on a booking. Null on the row means nobody has marked it yet. A no-show changes no balance. */
/** What the owner sent from Home (migration 0024). */
export const ownerNudgeKinds = ["quiet", "pay_reminder", "renewal"] as const;
export const ownerNudgeKindSchema = z.enum(ownerNudgeKinds);
export type OwnerNudgeKind = z.infer<typeof ownerNudgeKindSchema>;

/**
 * Add anyway (migration 0082, #ai-feedback 2026-10-26): how the owner added a client whom no
 * package paid for. `owed`: the client owes one session, and the next package pays it. `free`: the
 * owner gave the session.
 */
export const ownerOverrides = ["owed", "free"] as const;
export const ownerOverrideSchema = z.enum(ownerOverrides);
export type OwnerOverride = z.infer<typeof ownerOverrideSchema>;

/**
 * The booking refusals about packages and payment. The owner (and staff, when the owner turns it on)
 * can add the client anyway after one of them; the session's own rules (full, started, cancelled,
 * booked already) stay.
 */
export const overridableBookingCodes = [
  "GROUP_MISMATCH",
  "INSUFFICIENT_CREDITS",
  "PACKAGE_EXPIRED",
  "PACKAGE_PAUSED",
  "MEMBERSHIP_CAP_REACHED",
  "MEMBERSHIP_MONTH_UNPAID",
] as const;
export type OverridableBookingCode = (typeof overridableBookingCodes)[number];

export const attendanceMarks = ["present", "absent"] as const;
export const attendanceMarkSchema = z.enum(attendanceMarks);
export type AttendanceMark = z.infer<typeof attendanceMarkSchema>;

/**
 * How a staff member takes attendance on Desk: one card for each participant, or a list. Each
 * person picks one the first time and can switch at any time. Null on the account until then.
 */
export const attendanceModes = ["cards", "list"] as const;
export const attendanceModeSchema = z.enum(attendanceModes);
export type AttendanceMode = z.infer<typeof attendanceModeSchema>;

/**
 * How a mark was made (I1, check-in by name): `checkin` is one tap for each client who comes, saved
 * at once (businesses where clients pick each class); `headcount` is the cards flow and `list` the
 * list flow, which save the whole register (every business). The analytics of `attendance_marked`
 * carry it; the API sets `bookings.checked_in_at` for a `checkin` mark.
 */
export const attendanceMethods = ["checkin", "headcount", "list"] as const;
export const attendanceMethodSchema = z.enum(attendanceMethods);
export type AttendanceMethod = z.infer<typeof attendanceMethodSchema>;

/**
 * `unsorted`: a person from a WhatsApp group sync that the owner has not sorted yet (migration
 * 0033). The Families page and the join flow treat them as unknown until they are sorted.
 */
export const importClassifications = ["member", "team", "skip", "self", "unsorted"] as const;
export const importClassificationSchema = z.enum(importClassifications);
export type ImportClassification = z.infer<typeof importClassificationSchema>;

export const groupLinkPurposes = ["members", "team"] as const;
export const groupLinkPurposeSchema = z.enum(groupLinkPurposes);
export type GroupLinkPurpose = z.infer<typeof groupLinkPurposeSchema>;

export const groupLinkStatuses = ["waiting", "connected", "failed"] as const;
export const groupLinkStatusSchema = z.enum(groupLinkStatuses);
export type GroupLinkStatus = z.infer<typeof groupLinkStatusSchema>;

/** Where an imported contact list came from. Only `whatsapp_group` needs a provider that can read groups. */
export const importSources = ["whatsapp_group", "chat_export", "pasted", "contacts"] as const;
export const importSourceSchema = z.enum(importSources);
export type ImportSource = z.infer<typeof importSourceSchema>;

/** What the owner sorts a synced person as. More than one is fine: a coach can also be a parent. */
export const syncRoles = ["parent", "swimmer", "coach"] as const;
export const syncRoleSchema = z.enum(syncRoles);
export type SyncRole = z.infer<typeof syncRoleSchema>;

/** Whether the current period's package has been paid for. */
export const balancePaymentStatuses = ["paid", "owed"] as const;
export const balancePaymentStatusSchema = z.enum(balancePaymentStatuses);
export type BalancePaymentStatus = z.infer<typeof balancePaymentStatusSchema>;

/**
 * The eight owner setup steps. A business where clients book for themselves has seven: its order is
 * `setupOrder` in `business-shape.ts`. Completion rules live in the API's setup service.
 */
export const setupStepKeys = [
  "business",
  "team",
  "members_imported",
  "sessions",
  "packages",
  "invite_sent",
  "placement",
  "balances",
] as const;
export const setupStepKeySchema = z.enum(setupStepKeys);
export type SetupStepKey = z.infer<typeof setupStepKeySchema>;

/** Reason labels an owner picks when adding or removing sessions by hand. Shown to the member verbatim. */
export const adjustmentReasons = {
  add: ["Courtesy", "Paid in cash", "Correction", "We cancelled"],
  remove: ["Correction", "No-show", "Used elsewhere"],
} as const;
